import { ServiceItem, ServiceTransaction } from '../types';
import { bestandteilSperrText, waivedQuantity } from './projectLv';
// Blatt-Modul (Typen + flatRateLogic) — kein Zyklus.
import { hatBestandteile, istAbrechnungGesperrt, paketDeckel } from './bestandteile/bestandteilLogic';

/**
 * MANUELLE VERBUCHUNG OHNE BELEG — eine erbrachte Menge, die in der
 * Vergangenheit abgerechnet wurde, ohne dass es dazu (noch) einen Beleg gibt,
 * den die App einlesen koennte.
 *
 * Der Anlass: Bestandsprojekte, deren Rechnungen im alten System liegen. Die
 * Menge steht in der Buchhaltungsliste, im Rechnungs-Picker und im
 * Rechnungslauf als offen, obwohl sie laengst gefordert wurde. Wer die
 * Rechnung als PDF hat, liest sie ein ("Letzte Rechnung einlesen", ALT-Zeilen)
 * — wer sie nicht hat, konnte den Stand bisher nur per SQL geradeziehen.
 *
 * DIE ABGRENZUNG ZU DEN DREI NACHBARVORGAENGEN, und sie ist der ganze Punkt:
 *
 *   Alt-Rechnung einlesen — es GIBT einen Beleg. Er traegt Nummer, Datum und
 *                           Betrag, der Betrag wird zusaetzlich als
 *                           Vorrechnung abgezogen. Immer der bessere Weg.
 *   Manuelle Verbuchung   — die Menge wurde abgerechnet, der Beleg fehlt.
 *   (hier)                  `quantityBilled` steigt, `quantityUsed` bleibt.
 *   Verzicht              — die Menge wurde NIE abgerechnet und wird auch
 *                           nicht mehr gefordert (serviceWaiverLogic).
 *   Ruecknahme            — die Leistung gab es nie (serviceReversalLogic).
 *
 * Die Verwechslungsgefahr ist geldrelevant: eine manuelle Verbuchung behauptet
 * eine Forderung an den Kunden, die stattgefunden hat. Stimmt das nicht, fehlt
 * das Geld dauerhaft, denn die Menge wird nie wieder angeboten. Deshalb
 * verlangt der Vorgang ein DATUM und — solange keine Belegnummer angegeben
 * ist — eine Begruendung, und deshalb traegt jede Zeile den Text
 * "Manuelle Verbuchung ohne Beleg": daran erkennen Leistungsnachweis, Cockpit
 * und jede spaetere Pruefung, dass hinter dieser Menge kein Dokument steht
 * (serviceLogUtils.isManualPosting, projectSplit.altCoveredByParty).
 *
 * Pure Funktionen, kein Supabase, kein Date.now() — "jetzt" wird immer
 * hereingereicht. Wie serviceWaiverLogic und invoiceLogic; im Projekt ist das
 * die Bedingung dafuer, dass ueberhaupt getestet werden kann (kein jsdom).
 */

/**
 * Praefix der Buchungsreferenz.
 *
 * Bewusst NICHT "BU-…": das gehoert dem Rechnungsmodul (BU-INV/BU-STO), und
 * die Buchhaltungsansicht entscheidet an genau diesem Muster, ob eine
 * abgerechnete Position nur ein Storno zuruecknehmen darf. Ein eigenes Praefix
 * haelt die beiden Herkuenfte auseinander — die mit Beleg und die ohne.
 *
 * `serviceLogUtils.isAccountingEntry` kennt es: die Zeile ist ein
 * Abrechnungsvorgang und keine erbrachte Leistung. Sie darf in keiner
 * Zeitraum-Mengenrechnung als Leistung mitzaehlen, sonst boete die naechste
 * Rechnung dieselbe Menge ein zweites Mal an.
 */
export const MANUAL_BILLING_REFERENCE_PREFIX = 'MB-';

/**
 * Der Text, an dem projektweit erkannt wird, dass hinter der Menge KEIN
 * Dokument steht.
 *
 * Steht bewusst in der Beschreibung und nicht nur in der Referenz: mehrere
 * Auswertungen pruefen seit jeher den TEXT und nicht das Praefix
 * (generatorPerformance.isDrawdownEntry, cockpitLogic.isWorkEntry,
 * AdminDashboard). Sie alle erkennen die Zeile damit ohne eigene Anpassung.
 */
export const MANUAL_BILLING_MARKER = 'Manuelle Verbuchung ohne Beleg';

/** Kuerzeste Begruendung, die noch eine ist. Wie beim Verzicht. */
const MIN_REASON_LENGTH = 3;

/** Auf drei Nachkommastellen — dieselbe Toleranz wie im uebrigen Mengenrechnen. */
const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Kalendertag eines Zeitstempels. */
const day = (iso: string): string => (iso || '').slice(0, 10);

export interface ManualBillingInput {
    /** Menge, die ZUSAETZLICH als abgerechnet gilt (nicht der neue Gesamtstand). */
    quantity: number;
    /**
     * Tag, an dem abgerechnet wurde (JJJJ-MM-TT). PFLICHT.
     *
     * Er datiert die Protokollzeile. Ohne Datum gehoerte die Menge zu keinem
     * Monat und fiele durch jede Zeitraumauswertung — derselbe Riegel wie beim
     * Einlesen einer Alt-Rechnung (Projekt 5634).
     */
    billedOn: string;
    /** Nummer des Belegs, falls sie bekannt ist. Freiwillig — daher der Vorgang. */
    invoiceNumber?: string;
    /** PFLICHT, solange keine Belegnummer angegeben ist. */
    reason?: string;
    /**
     * Auftraggeber, dem die Menge berechnet wurde.
     *
     * Bei einem GETEILTEN Projekt Pflicht (`splitProject`): ohne Stempel ist
     * die Menge Altbestand ohne Zuordnung und sperrt spaeter jede Teilrechnung
     * (projectSplit.billedByParty → `unassigned`).
     */
    customerId?: string;
    /**
     * Der Teilnehmer der Aufteilung (project_splits.id) — bei geteiltem Projekt
     * ebenfalls Pflicht. Der Auftraggeber allein reicht nicht mehr, seit ein
     * Auftraggeber mehrere Abschnitte haben kann: die Menge waere zwischen
     * ihnen nicht zu entscheiden und sperrte wie ganz ohne Stempel.
     */
    splitPartyId?: string;
    /** Hat das Projekt einen Aufteilungsschluessel? Der Aufrufer weiss es. */
    splitProject?: boolean;
    actorName: string;
    actorId?: string;
    /** ISO-Zeitpunkt, hereingereicht. */
    now: string;
    /** Kennung der Protokollzeile, hereingereicht (crypto.randomUUID im Aufrufer). */
    entryId: string;
    /** Buchungsreferenz, hereingereicht (MANUAL_BILLING_REFERENCE_PREFIX + Kennung). */
    reference: string;
}

export interface ManualBillingPlan {
    /** Die Position mit erhoehtem Abrechnungsstand — ersetzt die alte im LV. */
    service: ServiceItem;
    /** Die Buchungszeile. Traegt die MENGE, denn sie IST der Abrechnungsvorgang. */
    entry: ServiceTransaction;
    /** Text fuer die Projektnotiz. */
    noteText: string;
}

/**
 * Flaches Ergebnis statt unterschiedener Vereinigung — dieselbe Ueberlegung
 * wie in serviceWaiverLogic: das Projekt uebersetzt ohne `strictNullChecks`,
 * und dort verengt TypeScript eine Vereinigung ueber ein boolesches Merkmal
 * nicht. Die Aufrufer pruefen beides.
 */
export interface ManualBillingResult {
    ok: boolean;
    plan?: ManualBillingPlan;
    error?: string;
}

/**
 * Menge, die an dieser Position ueberhaupt noch von Hand verbucht werden kann:
 * erbracht minus abgerechnet minus verzichtet.
 *
 * Die Obergrenze ist `quantityUsed` und nicht `quantityContracted`: abgerechnet
 * werden kann nur, was auch erbracht wurde. Steht auf dem alten Beleg mehr, als
 * das Projekt an Leistung kennt, fehlt die Leistung — sie gehoert nachgetragen
 * (Sammelnachtrag), nicht hier hineingerechnet. Sonst entstuende eine Position
 * mit "mehr abgerechnet als erbracht", die jede Bestandspruefung zu Recht
 * anmahnt und die im Leistungsnachweis nicht belegbar ist.
 *
 * PAKETPOSITION (docs/positionsbestandteile-plan.md, Kernregeln 2–4): dieselben
 * Regeln wie fuer jede Rechnung. Gate gesperrt oder unbekannt → 0 (der Knopf
 * „Als abgerechnet buchen" erscheint dann nicht, planManualBilling lehnt mit
 * Grund ab); sonst hoechstens der Deckel (Vertragsmenge minus abgerechnet) —
 * eine doppelte Positionsbuchung wird auch von Hand nie doppelt verbucht.
 */
export const manuallyBillableQuantity = (service: ServiceItem): number => {
    if (istAbrechnungGesperrt(service)) return 0;
    const roh = (service.quantityUsed || 0) - (service.quantityBilled || 0) - waivedQuantity(service);
    return Math.max(0, round3(Math.min(roh, paketDeckel(service))));
};

/** Alle manuellen Verbuchungen einer Position — Buchungen und Ruecknahmen. */
export const manualBillingEntries = (
    log: readonly ServiceTransaction[],
    serviceId: string,
): ServiceTransaction[] =>
    (log || []).filter(
        (t) =>
            t?.serviceId === serviceId &&
            typeof t.bookingReference === 'string' &&
            t.bookingReference.startsWith(MANUAL_BILLING_REFERENCE_PREFIX),
    );

/**
 * Von Hand verbuchte Menge einer Position — Ruecknahmen bereits verrechnet.
 *
 * Erst summieren, dann bei 0 kappen: so haengt das Ergebnis nicht von der
 * Reihenfolge der Zeilen ab (dieselbe Regel wie projectSplit.billedByParty).
 */
export const manuallyBilledQuantity = (
    log: readonly ServiceTransaction[],
    serviceId: string,
): number =>
    Math.max(
        0,
        round3(manualBillingEntries(log, serviceId).reduce((acc, t) => acc + (t.quantity || 0), 0)),
    );

/**
 * Belegnummer einer manuellen Verbuchung, falls eine angegeben wurde.
 *
 * Aus dem Text gelesen und nicht aus einem eigenen Feld: die Nummer ist hier
 * eine Notiz und keine Zusage — es gibt zu ihr kein Dokument in der App, und
 * ein Feld dafuer legte nahe, dass sich etwas darauf stuetzen liesse. Der Text
 * "ohne Belegnummer" traegt kein Leerzeichen nach "Beleg" und faellt damit
 * heraus.
 */
export const manualBillingInvoiceNumber = (entry: ServiceTransaction): string => {
    const match = /Beleg\s+([^\s,—]+)/.exec(entry?.description || '');
    return match ? match[1] : '';
};

/**
 * Eine manuelle Verbuchung planen. Schreibt nichts, entscheidet nur.
 *
 * Zu viel zu verbuchen ist ein FEHLER und wird nicht stillschweigend auf das
 * Moegliche gekuerzt — ein stiller Deckel sieht fuer den Anwender aus wie ein
 * Erfolg, und die Position steht beim naechsten Blick weiter offen, ohne dass
 * jemand erfuehre warum (dieselbe Ueberlegung wie beim Verzicht).
 */
export const planManualBilling = (
    service: ServiceItem,
    input: ManualBillingInput,
): ManualBillingResult => {
    const available = manuallyBillableQuantity(service);
    const quantity = round3(input.quantity);
    const invoiceNumber = (input.invoiceNumber || '').trim();
    const reason = (input.reason || '').trim();
    const unit = service.unit || '';

    // PAKETPOSITION — vor jeder anderen Pruefung: ist das Gate zu, sagt
    // „nichts offen" weiter unten das Falsche. Eine von Hand als abgerechnet
    // gebuchte Paketposition mit offenen Bestandteilen haette das Gate
    // umgangen; ein unbekannter Stand sperrt genauso (fail closed).
    if (istAbrechnungGesperrt(service)) {
        return {
            ok: false,
            error: `Paketposition: ${bestandteilSperrText(service)} — erst verbuchen, wenn alle erledigt oder entfallen sind.`,
        };
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
        return { ok: false, error: 'Bitte eine Menge größer als 0 angeben.' };
    }
    if (hatBestandteile(service) && quantity > available) {
        // Der Deckel: mehr als Vertragsmenge minus abgerechnet ist bei einem
        // Paket nie eine fehlende Leistung (der Text darunter), sondern eine
        // doppelte Positionsbuchung.
        return {
            ok: false,
            error: `Paketposition: höchstens ${available} ${unit} (Vertragsmenge minus abgerechnet) — eine doppelte Buchung darf nicht doppelt verbucht werden.`.replace(
                /\s+/g,
                ' ',
            ),
        };
    }
    if (available <= 0) {
        return {
            ok: false,
            error: 'An dieser Position ist nichts offen, was noch als abgerechnet gebucht werden könnte.',
        };
    }
    if (quantity > available) {
        return {
            ok: false,
            error: `Es sind nur ${available} ${unit} offen. Mehr kann nicht als abgerechnet gebucht werden — fehlt die Leistung im Projekt, gehört sie zuerst nachgetragen.`.replace(
                /\s+/g,
                ' ',
            ),
        };
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test((input.billedOn || '').trim())) {
        return {
            ok: false,
            error: 'Bitte das Datum der Abrechnung angeben (JJJJ-MM-TT). Ohne Datum gehört die Menge zu keinem Monat und fehlt in jeder Zeitraumabrechnung.',
        };
    }
    if (input.billedOn.trim() > day(input.now)) {
        // Abgerechnet wurde in der VERGANGENHEIT — dafuer gibt es den Vorgang.
        // Ein Datum in der Zukunft ist immer ein Tippfehler, und es zoege den
        // Leistungszeitraum der naechsten Rechnung mit sich.
        return { ok: false, error: 'Das Datum der Abrechnung darf nicht in der Zukunft liegen.' };
    }
    if (!invoiceNumber && reason.length < MIN_REASON_LENGTH) {
        // Entweder die Nummer oder der Grund — irgendetwas muss die Buchung
        // tragen. Ohne beides waere sie im Streitfall und bei jeder spaeteren
        // Pruefung eine Zahl ohne Herkunft.
        return {
            ok: false,
            error: 'Ohne Belegnummer ist eine Begründung Pflicht — sie steht später im Protokoll.',
        };
    }
    if (input.splitProject && !(input.splitPartyId || '').trim()) {
        return {
            ok: false,
            error: 'Dieses Projekt ist aufgeteilt. Bitte angeben, wem die Menge berechnet wurde — ohne Zuordnung sperrt sie später jede Teilrechnung.',
        };
    }
    if (service.flatCoveredBy) {
        // Eine von einer Monatspauschale gedeckte Position friert geldlich auf
        // `quantityBilled` ein (siehe types.ts, FlatRateCoverage). Diesen Stand
        // anzuheben forderte Geld, das die Pauschale bereits abgilt.
        return {
            ok: false,
            error: 'Diese Position ist durch eine Monatspauschale gedeckt — ihr Geldstand darf nicht von Hand verändert werden.',
        };
    }

    const newBilled = round3((service.quantityBilled || 0) + quantity);
    const herkunft = invoiceNumber ? `Beleg ${invoiceNumber}` : 'ohne Belegnummer';
    const datum = input.billedOn.trim();

    return {
        ok: true,
        plan: {
            service: {
                ...service,
                quantityBilled: newBilled,
                // Nur die volle Deckung setzt den Status. Sonst bleibt er, wie
                // er ist: eine teilweise abgerechnete Position auf 'done' zu
                // heben behauptete, es sei nichts mehr zu tun.
                ...(newBilled >= (service.quantityUsed || 0) ? { status: 'billed' as const } : {}),
            },
            entry: {
                id: input.entryId,
                // Datum der ABRECHNUNG, nicht des heutigen Vorgangs: die Zeile
                // gehoert in den Monat, in dem gefordert wurde.
                date: datum,
                serviceId: service.id,
                // Die Menge steht hier und ist nicht 0 (anders als bei Verzicht
                // und Ruecknahme): diese Zeile IST die Abrechnung, und der
                // Stand je Auftraggeber wird aus genau solchen Zeilen
                // abgeleitet (projectSplit.billedByParty).
                quantity,
                performerId: input.actorId || '',
                performerName: input.actorName,
                isSubstitution: false,
                description:
                    `${MANUAL_BILLING_MARKER}: ${quantity} ${unit}, ${herkunft}, abgerechnet am ${datum}` +
                    (reason ? ` — ${reason}` : ''),
                bookedAt: input.now,
                bookedBy: input.actorName,
                bookedById: input.actorId,
                bookingReference: input.reference,
                customerId: (input.customerId || '').trim() || undefined,
                splitPartyId: (input.splitPartyId || '').trim() || undefined,
            },
            noteText:
                `Von Hand als abgerechnet gebucht: ${quantity} ${unit} ` +
                `„${service.title}" (${service.lvPosition || 'ohne LV-Nr.'}), ${herkunft}, ` +
                `abgerechnet am ${datum}.` +
                (reason ? ` Grund: ${reason}` : ''),
        },
    };
};

/**
 * Eine manuelle Verbuchung zuruecknehmen.
 *
 * Die urspruengliche Zeile bleibt STEHEN und bekommt eine negative Gegenzeile.
 * Sie zu loeschen waere bequemer und falsch: das Protokoll ist Beweismittel,
 * und wer spaeter fragt, warum ein Stand einmal hoeher war, findet sonst
 * nichts. `billedByParty` verrechnet negative Zeilen ohnehin (dieselbe Form
 * wie das Storno).
 */
export const planManualBillingUndo = (
    service: ServiceItem,
    log: readonly ServiceTransaction[],
    input: Omit<ManualBillingInput, 'quantity' | 'billedOn' | 'invoiceNumber'> & {
        /** Kennung der zurueckzunehmenden Buchungszeile. */
        targetEntryId: string;
    },
): ManualBillingResult => {
    const target = manualBillingEntries(log, service.id).find((t) => t.id === input.targetEntryId);
    if (!target) {
        return { ok: false, error: 'Diese manuelle Verbuchung wurde am Projekt nicht gefunden.' };
    }
    const quantity = round3(target.quantity || 0);
    if (quantity <= 0) {
        return { ok: false, error: 'Diese Zeile ist bereits eine Rücknahme.' };
    }
    const alreadyUndone = manualBillingEntries(log, service.id).some(
        (t) => (t.quantity || 0) < 0 && t.description?.includes(input.targetEntryId),
    );
    if (alreadyUndone) {
        return { ok: false, error: 'Diese manuelle Verbuchung wurde bereits zurückgenommen.' };
    }
    const current = service.quantityBilled || 0;
    if (round3(current - quantity) < 0) {
        return {
            ok: false,
            error: 'Der Abrechnungsstand dieser Position ist inzwischen niedriger als die Buchung — bitte zuerst prüfen, was ihn gesenkt hat.',
        };
    }

    const unit = service.unit || '';
    const reason = (input.reason || '').trim();
    const newBilled = round3(current - quantity);

    return {
        ok: true,
        plan: {
            service: {
                ...service,
                quantityBilled: newBilled,
                // Der Status faellt mit: die Position ist nicht mehr voll
                // abgerechnet. 'done' und nicht 'open' — erbracht wurde sie.
                ...(service.status === 'billed' && newBilled < (service.quantityUsed || 0)
                    ? { status: 'done' as const }
                    : {}),
            },
            entry: {
                id: input.entryId,
                // Tag der RUECKNAHME. Sie gehoert in den Monat, in dem
                // entschieden wurde — die Buchung selbst behaelt ihr Datum.
                date: day(input.now),
                serviceId: service.id,
                quantity: -quantity,
                performerId: input.actorId || '',
                performerName: input.actorName,
                isSubstitution: false,
                description:
                    `${MANUAL_BILLING_MARKER} zurückgenommen: ${quantity} ${unit} ` +
                    `(Buchung ${input.targetEntryId})` +
                    (reason ? ` — ${reason}` : ''),
                bookedAt: input.now,
                bookedBy: input.actorName,
                bookedById: input.actorId,
                bookingReference: input.reference,
                customerId: target.customerId,
                // Die Ruecknahme erbt die Zuordnung des Originals. Ohne sie
                // waere die Gegenbuchung Altbestand ohne Teilnehmer und der
                // Stand des Abschnitts bliebe um die zurueckgenommene Menge zu
                // hoch.
                splitPartyId: target.splitPartyId,
            },
            noteText:
                `Manuelle Verbuchung zurückgenommen: ${quantity} ${unit} ` +
                `„${service.title}" (${service.lvPosition || 'ohne LV-Nr.'}) steht wieder zur Abrechnung.` +
                (reason ? ` Grund: ${reason}` : ''),
        },
    };
};
